import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, AlertTriangle, CheckCircle, Wallet, Filter, Users, ChevronDown, ChevronUp, Clock, Download, FileText, FileSpreadsheet } from "lucide-react";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const debtTypeLabels = {
  dette_recette: { label: "Dette recette", color: "bg-red-100 text-red-800 border-red-300" },
  amende: { label: "Amende", color: "bg-orange-100 text-orange-800 border-orange-300" },
  pret: { label: "Pret", color: "bg-violet-100 text-violet-800 border-violet-300" },
  autre: { label: "Autre", color: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function DebtManagementPage() {
  const [debts, setDebts] = useState([]);
  const [repayments, setRepayments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filterDriver, setFilterDriver] = useState("all");
  const [filterStatut, setFilterStatut] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [expandedDebts, setExpandedDebts] = useState({});
  const [form, setForm] = useState({ chauffeur_id: "", type: "amende", montant: "", description: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [dRes, drRes, rpRes] = await Promise.all([
        api.get("/debts"), api.get("/users?role=chauffeur"), api.get("/repayments")
      ]);
      setDebts(dRes.data); setDrivers(drRes.data); setRepayments(rpRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/debts", { ...form, montant: parseFloat(form.montant) });
      toast.success("Dette enregistree");
      setShowAdd(false);
      setForm({ chauffeur_id: "", type: "amende", montant: "", description: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleRepayment = async (isFullPayment = false) => {
    if (!selectedDebt) return;
    const amount = isFullPayment ? selectedDebt.montant_restant : parseFloat(paymentAmount);
    if (!amount || amount <= 0) return;
    try {
      await api.post(`/debts/${selectedDebt.id}/repayments`, { montant: amount, note: paymentNote });
      const remaining = selectedDebt.montant_restant - amount;
      toast.success(remaining <= 0 ? "Dette entierement remboursee" : `${amount} Frc rembourse`);
      setShowPayment(false); setSelectedDebt(null); setPaymentAmount(""); setPaymentNote("");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openPaymentDialog = (debt, full = false) => {
    setSelectedDebt(debt);
    setPaymentAmount(full ? String(debt.montant_restant) : "");
    setPaymentNote("");
    setShowPayment(true);
  };

  const toggleExpand = (debtId) => {
    setExpandedDebts(prev => ({ ...prev, [debtId]: !prev[debtId] }));
  };

  const getDriverName = (id) => { const d = drivers.find(dr => dr.id === id); return d ? `${d.prenom} ${d.nom}` : "-"; };
  const getTypeInfo = (type) => debtTypeLabels[type] || debtTypeLabels.autre;
  const getDebtRepayments = (debtId) => repayments.filter(r => r.debt_id === debtId);
  const getTotalRepaid = (debtId) => getDebtRepayments(debtId).reduce((s, r) => s + (r.montant || 0), 0);

  const filtered = debts.filter(d => {
    if (filterDriver !== "all" && d.chauffeur_id !== filterDriver) return false;
    if (filterStatut !== "all" && d.statut !== filterStatut) return false;
    return true;
  });

  const activeDebts = debts.filter(d => d.statut === "en_cours");
  const totalDette = activeDebts.reduce((s, d) => s + (d.montant_restant || 0), 0);
  const driversWithDebt = [...new Set(activeDebts.map(d => d.chauffeur_id))].length;

  const debtExportCols = [
    { label: "Chauffeur", accessor: (d) => getDriverName(d.chauffeur_id) },
    { label: "Type", accessor: (d) => (debtTypeLabels[d.type] || debtTypeLabels.autre).label },
    { label: "Description", key: "description" },
    { label: "Montant initial", key: "montant" },
    { label: "Montant restant", key: "montant_restant" },
    { label: "Statut", accessor: (d) => d.statut === "en_cours" ? "En cours" : "Rembourse" },
    { label: "Date", accessor: (d) => new Date(d.created_at).toLocaleDateString("fr-FR") },
  ];

  const debtsByDriver = {};
  filtered.forEach(d => {
    if (!debtsByDriver[d.chauffeur_id]) debtsByDriver[d.chauffeur_id] = [];
    debtsByDriver[d.chauffeur_id].push(d);
  });

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="debt-management-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Gestion des dettes</h1>
          <p className="text-sm text-slate-500 mt-1">{activeDebts.length} dette(s) en cours</p>
        </div>
        <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => setShowAdd(true)} data-testid="btn-add-debt">
          <Plus className="w-4 h-4 mr-2" />Enregistrer une dette
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="debt-summary-cards">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center"><Wallet className="w-5 h-5 text-red-600" /></div></div>
          <p className="text-2xl font-bold text-red-600 font-heading">{totalDette.toLocaleString("fr-FR")} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Total dettes en cours</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Users className="w-5 h-5 text-amber-600" /></div></div>
          <p className="text-2xl font-bold text-amber-600 font-heading">{driversWithDebt}</p>
          <p className="text-xs text-slate-500 mt-1">Chauffeur(s) endette(s)</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div></div>
          <p className="text-2xl font-bold text-emerald-600 font-heading">{debts.filter(d => d.statut === "rembourse").length}</p>
          <p className="text-xs text-slate-500 mt-1">Dette(s) remboursee(s)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <Filter className="w-4 h-4 text-slate-400" />
        <Select value={filterDriver} onValueChange={setFilterDriver}>
          <SelectTrigger className="w-[220px]" data-testid="filter-driver"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les chauffeurs</SelectItem>
            {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.prenom} {d.nom}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-[180px]" data-testid="filter-statut"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="en_cours">En cours</SelectItem>
            <SelectItem value="rembourse">Rembourse</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-export-debts">
                <Download className="w-4 h-4 mr-2" />Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportCSV(filtered, debtExportCols, "dettes")} data-testid="export-debts-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportExcel(filtered, debtExportCols, "dettes")} data-testid="export-debts-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF(filtered, debtExportCols, "dettes", "Gestion des dettes")} data-testid="export-debts-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Debts by Driver */}
      {Object.entries(debtsByDriver).map(([driverId, driverDebts]) => {
        const driverTotal = driverDebts.filter(d => d.statut === "en_cours").reduce((s, d) => s + (d.montant_restant || 0), 0);
        return (
          <Card key={driverId} className="mb-6" data-testid={`driver-debts-${driverId}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${driverTotal > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
                    {driverTotal > 0 ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <CheckCircle className="w-5 h-5 text-emerald-600" />}
                  </div>
                  <div>
                    <CardTitle className="text-base font-heading">{getDriverName(driverId)}</CardTitle>
                    <p className="text-xs text-slate-500">{driverDebts.length} dette(s) - {driverDebts.filter(d => d.statut === "en_cours").length} en cours</p>
                  </div>
                </div>
                {driverTotal > 0 && (
                  <Badge variant="outline" className="text-base px-3 py-1 bg-red-50 text-red-700 border-red-200 font-heading font-bold">{driverTotal.toLocaleString("fr-FR")} Frc</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-semibold text-slate-600 w-8"></TableHead>
                    <TableHead className="font-semibold text-slate-600">Type</TableHead>
                    <TableHead className="font-semibold text-slate-600">Description</TableHead>
                    <TableHead className="font-semibold text-slate-600">Initial</TableHead>
                    <TableHead className="font-semibold text-slate-600">Rembourse</TableHead>
                    <TableHead className="font-semibold text-slate-600">Restant</TableHead>
                    <TableHead className="font-semibold text-slate-600">Date</TableHead>
                    <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                    <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {driverDebts.map(debt => {
                    const typeInfo = getTypeInfo(debt.type);
                    const debtRepayments = getDebtRepayments(debt.id);
                    const totalRepaid = getTotalRepaid(debt.id);
                    const isExpanded = expandedDebts[debt.id];
                    const hasRepayments = debtRepayments.length > 0;
                    return (
                      <React.Fragment key={debt.id}>
                        <TableRow data-testid={`debt-row-${debt.id}`} className={`${debt.statut === "rembourse" ? "opacity-60" : ""} ${hasRepayments ? "cursor-pointer" : ""}`} onClick={() => hasRepayments && toggleExpand(debt.id)}>
                          <TableCell className="w-8 px-2">
                            {hasRepayments && (
                              <div className="text-slate-400" data-testid={`btn-expand-${debt.id}`}>
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </div>
                            )}
                          </TableCell>
                          <TableCell><Badge variant="outline" className={typeInfo.color}>{typeInfo.label}</Badge></TableCell>
                          <TableCell className="text-slate-700 max-w-[200px] truncate">{debt.description || "-"}</TableCell>
                          <TableCell className="text-slate-600">{debt.montant} Frc</TableCell>
                          <TableCell className="text-emerald-600 font-medium">{totalRepaid > 0 ? `${totalRepaid} Frc` : "-"}</TableCell>
                          <TableCell className={`font-semibold ${debt.montant_restant > 0 ? "text-red-600" : "text-emerald-600"}`}>{debt.montant_restant} Frc</TableCell>
                          <TableCell className="text-slate-500 text-sm">{new Date(debt.created_at).toLocaleDateString("fr-FR")}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={debt.statut === "en_cours" ? "status-impaye" : "status-actif"}>
                              {debt.statut === "en_cours" ? "En cours" : "Rembourse"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            {debt.statut === "en_cours" && (
                              <div className="flex gap-1 justify-end">
                                <Button variant="ghost" size="sm" className="text-[#0F52BA] text-xs" onClick={() => openPaymentDialog(debt)} data-testid={`btn-partial-pay-${debt.id}`}>
                                  Paiement partiel
                                </Button>
                                <Button variant="ghost" size="sm" className="text-emerald-600 text-xs" onClick={() => openPaymentDialog(debt, true)} data-testid={`btn-full-pay-${debt.id}`}>
                                  Tout payer
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                        {/* Repayment history rows */}
                        {isExpanded && debtRepayments.map(rp => (
                          <TableRow key={rp.id} className="bg-emerald-50/40 border-l-2 border-emerald-400" data-testid={`repayment-row-${rp.id}`}>
                            <TableCell></TableCell>
                            <TableCell colSpan={2} className="text-sm">
                              <div className="flex items-center gap-2 text-emerald-700">
                                <Clock className="w-3.5 h-3.5" />
                                <span className="font-medium">Remboursement</span>
                                {rp.note && <span className="text-slate-500">- {rp.note}</span>}
                              </div>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell className="text-emerald-700 font-semibold text-sm">+{rp.montant} Frc</TableCell>
                            <TableCell></TableCell>
                            <TableCell className="text-slate-400 text-xs">{new Date(rp.created_at).toLocaleDateString("fr-FR")} {new Date(rp.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {filtered.length === 0 && (
        <div className="text-center text-slate-500 py-16">
          <Wallet className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="font-medium">Aucune dette trouvee</p>
          <p className="text-sm mt-1">Tous les chauffeurs sont a jour.</p>
        </div>
      )}

      {/* Add Debt Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-debt-dialog">
          <DialogHeader><DialogTitle className="font-heading">Enregistrer une dette</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Chauffeur</Label>
              <Select value={form.chauffeur_id} onValueChange={v => setForm({...form, chauffeur_id: v})}>
                <SelectTrigger data-testid="select-debt-driver"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.prenom} {d.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                <SelectTrigger data-testid="select-new-debt-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="amende">Amende</SelectItem>
                  <SelectItem value="pret">Pret</SelectItem>
                  <SelectItem value="dette_recette">Dette recette</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Montant (Frc)</Label><Input type="number" step="0.01" value={form.montant} onChange={e => setForm({...form, montant: e.target.value})} required data-testid="input-new-debt-montant" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Raison de la dette..." data-testid="input-new-debt-desc" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-new-debt">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Repayment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent data-testid="repayment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Enregistrer un remboursement</DialogTitle></DialogHeader>
          {selectedDebt && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm"><span className="text-slate-500">Chauffeur :</span> <span className="font-medium">{getDriverName(selectedDebt.chauffeur_id)}</span></p>
                <p className="text-sm"><span className="text-slate-500">Type :</span> <Badge variant="outline" className={getTypeInfo(selectedDebt.type).color}>{getTypeInfo(selectedDebt.type).label}</Badge></p>
                <p className="text-sm"><span className="text-slate-500">Montant initial :</span> <span className="font-medium">{selectedDebt.montant} Frc</span></p>
                <p className="text-sm"><span className="text-slate-500">Deja rembourse :</span> <span className="font-medium text-emerald-600">{getTotalRepaid(selectedDebt.id)} Frc</span></p>
                <p className="text-sm"><span className="text-slate-500">Restant :</span> <span className="font-bold text-red-600">{selectedDebt.montant_restant} Frc</span></p>
                {/* Progress bar */}
                <div className="mt-2">
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(((selectedDebt.montant - selectedDebt.montant_restant) / selectedDebt.montant) * 100, 100)}%` }}></div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">{Math.round(((selectedDebt.montant - selectedDebt.montant_restant) / selectedDebt.montant) * 100)}% rembourse</p>
                </div>
              </div>
              <div>
                <Label>Montant du remboursement (Frc)</Label>
                <Input type="number" step="0.01" max={selectedDebt.montant_restant} value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder={`Max: ${selectedDebt.montant_restant} Frc`} data-testid="input-repayment-amount" />
              </div>
              <div>
                <Label>Note (optionnel)</Label>
                <Input value={paymentNote} onChange={e => setPaymentNote(e.target.value)} placeholder="Ex: paiement en especes..." data-testid="input-repayment-note" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPayment(false)}>Annuler</Button>
                <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => handleRepayment(false)} data-testid="btn-confirm-repayment">Confirmer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
